import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { pushCapability, readPushState } from "./pushNotifications";

const STORAGE_PREFIX = "venaqqi_push_nudge_v1:";
const SESSION_PREFIX = "venaqqi_push_nudge_session_v1:";
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_DISMISSALS = 3;
const MIN_SESSIONS = 2;
const DISPLAY_DELAY_MS = 45000;

const emptyState = () => ({ sessions: 0, dismissals: 0, lastDismissedAt: null, everEnabled: false });

export const shouldShowPushNudge = (state, now = Date.now()) => {
  if (!state || state.everEnabled) return false;
  if (Number(state.sessions || 0) < MIN_SESSIONS) return false;
  if (Number(state.dismissals || 0) >= MAX_DISMISSALS) return false;
  const dismissedAt = Number(state.lastDismissedAt || 0);
  if (dismissedAt && now - dismissedAt < DISMISS_COOLDOWN_MS) return false;
  return true;
};

const readNudgeState = userId => {
  try {
    const parsed = JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}${userId}`) || "null");
    return { ...emptyState(), ...(parsed || {}) };
  } catch {
    return emptyState();
  }
};

const writeNudgeState = (userId, state) => {
  try { localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify(state)); } catch { /* storage unavailable */ }
};

const registerSession = userId => {
  const state = readNudgeState(userId);
  try {
    const sessionKey = `${SESSION_PREFIX}${userId}`;
    if (!sessionStorage.getItem(sessionKey)) {
      sessionStorage.setItem(sessionKey, "1");
      state.sessions = Number(state.sessions || 0) + 1;
      writeNudgeState(userId, state);
    }
  } catch {
    // If sessionStorage is unavailable, do not manufacture extra visits.
  }
  return state;
};

export default function PushActivationNudge() {
  const [userId, setUserId] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    const applySession = session => {
      if (!active) return;
      setUserId(session?.user?.id || null);
      setVisible(false);
    };

    void supabase.auth.getSession().then(({ data }) => applySession(data?.session || null)).catch(() => {});
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => applySession(session));
    return () => { active = false; listener?.subscription?.unsubscribe?.(); };
  }, []);

  useEffect(() => {
    if (!userId || !pushCapability().supported) return undefined;
    let active = true;
    let timer;

    const evaluate = async () => {
      const stored = registerSession(userId);
      if (stored.everEnabled) return;
      try {
        const state = await readPushState({ supabase, userId });
        if (!active) return;
        if (state.subscribed) {
          writeNudgeState(userId, { ...stored, everEnabled: true });
          return;
        }
        if (state.permission === "denied" || !shouldShowPushNudge(stored)) return;
        timer = window.setTimeout(() => { if (active) setVisible(true); }, DISPLAY_DELAY_MS);
      } catch { /* the profile remains the source of truth */ }
    };

    void evaluate();
    return () => { active = false; if (timer) window.clearTimeout(timer); };
  }, [userId]);

  const dismiss = () => {
    if (userId) {
      const current = readNudgeState(userId);
      writeNudgeState(userId, {
        ...current,
        dismissals: Number(current.dismissals || 0) + 1,
        lastDismissedAt: Date.now(),
      });
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside aria-label="Recordatorio de notificaciones" style={{ position: "fixed", right: 14, bottom: 78, zIndex: 8500, width: "min(330px, calc(100vw - 28px))", background: "#111", border: "1px solid #343434", borderRadius: 14, padding: 14, boxShadow: "0 14px 40px rgba(0,0,0,.5)", color: "#eee" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div aria-hidden="true" style={{ fontSize: 19, lineHeight: 1 }}>🔔</div>
        <div style={{ minWidth: 0 }}>
          <strong style={{ display: "block", fontSize: 13, marginBottom: 5 }}>Avisos útiles, cuando tú quieras</strong>
          <p style={{ margin: 0, color: "#bbb", fontSize: 12, lineHeight: 1.5 }}>Activa las notificaciones en tu perfil para recibir avisos útiles de Venaqqi y Don D’.</p>
          <p style={{ margin: "5px 0 0", color: "#777", fontSize: 10, lineHeight: 1.4 }}>No bloquea nada. Puedes activarlas después desde Perfil.</p>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <button type="button" onClick={dismiss} style={{ border: "1px solid #3a3a3a", borderRadius: 9, background: "#191919", color: "#ccc", padding: "7px 10px", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Ahora no</button>
      </div>
    </aside>
  );
}
